import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { StaleDataBadge } from '@/components/StaleDataBadge';
import { cn } from '@/lib/utils';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /** Ícone exibido à esquerda do título dentro de uma caixa colorida. */
  icon?: ReactNode;
  actions?: ReactNode;
  /** Caminho + label para link de volta. Renderiza uma seta acima do título. */
  backTo?: { label: string; path: string };
  /** Timestamp da última atualização dos dados. Renderiza StaleDataBadge ao lado do título. */
  dataUpdatedAt?: number;
  /** Badge opcional (ex: status) renderizado ao lado do título. */
  badge?: ReactNode;
  /** Classes extras no contêiner (ex.: `sm:mb-0` numa tela que precisa da altura). */
  className?: string;
}

/**
 * No celular (docs/PLANO_RESPONSIVIDADE_MOBILE.md §3.1) o cabeçalho cabe numa linha: sem a
 * caixa do ícone (o menu já diz onde se está) e com as ações à direita do título. Para isso a
 * página entrega no celular só ícones (com `aria-label`), e a ação principal vai para o FAB.
 */
export function PageHeader({ title, subtitle, icon, actions, backTo, dataUpdatedAt, badge, className }: PageHeaderProps) {
  return (
    <div className={cn('mb-2 space-y-1 sm:mb-8 sm:space-y-2', className)}>
      {backTo && (
        <Link
          to={backTo.path}
          className="-ml-1 inline-flex min-h-11 items-center gap-1.5 px-1 text-sm text-muted-foreground transition-colors hover:text-foreground sm:min-h-0"
        >
          <ArrowLeft size={16} />
          {backTo.label}
        </Link>
      )}
      {/* Celular: o que cabe fica na linha do título; o que não cabe (telas do admin com
          botões de texto) desce para a linha de baixo. A partir de 640px, como sempre: uma
          linha, com as ações quebrando entre si. */}
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-2 sm:flex-nowrap sm:gap-3">
        <div className="flex items-start gap-3">
          {icon && (
            <div className="hidden rounded-lg bg-primary/10 p-2.5 sm:block">
              <span className="flex h-6 w-6 items-center justify-center text-primary [&>svg]:h-6 [&>svg]:w-6">
                {icon}
              </span>
            </div>
          )}
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight">
                {title}
              </h1>
              {badge}
              {dataUpdatedAt != null && dataUpdatedAt > 0 && (
                <StaleDataBadge dataUpdatedAt={dataUpdatedAt} />
              )}
            </div>
            {subtitle && (
              <p className="hidden text-sm text-muted-foreground sm:block">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {actions && (
          <div className="ml-auto flex flex-row flex-wrap items-center gap-1 sm:ml-0 sm:gap-2">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

export default PageHeader;
